// Shared visual atoms: squircle background, SnappPay logo, audio scrubber.
import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { api } from "./api.js";
import { C, WHITE, BRAND } from "../design-system.js";
import { useLang } from "../i18n.jsx";
import { BAZAAR_ICON } from "../brand-marks.js";
import { hostKey, platformInfo } from "./platform.js";
import { toFa } from "./format.js";

// ── Squircle (superellipse) ──────────────────────────────────────────────────
// Apple-style smooth corners for SQUARE surfaces (avatars, icon tiles). CSS
// `corner-shape` rendered sharp on the user's device, so we draw the shape as an
// SVG path instead. A normalized 0–100 viewBox with preserveAspectRatio="none"
// scales to any size with no ResizeObserver; non-scaling-stroke keeps the border
// crisp; glows survive via filter:drop-shadow (a mask would clip box-shadow).
// n controls roundness: lower = rounder (n→2 is a circle). One-UI-style icons sit
// around n≈3. `inset` keeps the path off the viewBox edges so the (non-scaling)
// stroke is never clipped by the element/container bounds or during press/scale.
export function buildSquirclePath(n = 3, steps = 88, inset = 4) {
  const a = 50 - inset, c = 50, pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    const ct = Math.cos(t), st = Math.sin(t);
    const x = c + Math.sign(ct) * a * Math.pow(Math.abs(ct), 2 / n);
    const y = c + Math.sign(st) * a * Math.pow(Math.abs(st), 2 / n);
    pts.push((i === 0 ? "M" : "L") + x.toFixed(2) + " " + y.toFixed(2));
  }
  return pts.join(" ") + " Z";
}
export const SQUIRCLE_D = buildSquirclePath(3);

// Absolutely-positioned squircle background (fill + border + optional glow).
// Drop it as the first child of a `position:relative` square container.
export function SquircleBg({ fill = "none", stroke = "none", strokeWidth = 0, glow }) {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%" aria-hidden
      style={{ position: "absolute", inset: 0, overflow: "visible", filter: glow ? `drop-shadow(${glow})` : undefined, pointerEvents: "none" }}>
      <path d={SQUIRCLE_D} fill={fill} stroke={stroke} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ── SnappPay logo ─────────────────────────────────────────────────────────────
// Official «Snapp! Pay» wordmark (snapppay.ir), traced to `currentColor` so one
// component serves both the solid-blue button (white) and any tinted variant.
// viewBox is tightened to the ink box so `height` sizes the visible mark exactly.
export function SnappPayLogo({ height = 28, style = {} }) {
  return (
    // Size via CSS, never the width/height ATTRIBUTES: `width="auto"` is not a
    // valid SVG attribute value and Android WebViews fall back to 100%, so the
    // logo swallows the whole flex row and pushes the label out of the button.
    <svg viewBox="0 17.4 80 47.5" fill="currentColor" aria-label="SnappPay"
      style={{ display: "block", height, width: "auto", flex: "0 0 auto", ...style }}>
      <path fillRule="evenodd" clipRule="evenodd" d="M6.0419 50.1517H4.53687V45.9942H6.0419C7.70855 45.9942 8.54152 46.6872 8.54152 48.073C8.54152 49.459 7.70855 50.1517 6.0419 50.1517ZM7.22267 42.5H2.251e-10V59.1745H4.53687V53.6457H7.22267C9.18197 53.6457 10.6835 53.1595 11.7251 52.1862C12.7668 51.2132 13.2878 49.842 13.2878 48.073C13.2878 46.3037 12.7668 44.9327 11.7251 43.9595C10.6835 42.9865 9.18197 42.5 7.22267 42.5Z"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M23.6467 54.5057C23.4995 54.8155 23.3067 55.0885 23.0677 55.3242C22.8285 55.5602 22.543 55.7442 22.2112 55.8767C21.8792 56.0097 21.5282 56.076 21.1582 56.076C20.772 56.076 20.4132 56.0097 20.0815 55.8767C19.7495 55.7442 19.4642 55.5602 19.225 55.3242C18.9857 55.0885 18.7967 54.812 18.6577 54.4947C18.519 54.178 18.4495 53.8352 18.4495 53.4665C18.4495 53.1127 18.519 52.7845 18.6577 52.4825C18.7967 52.1805 18.9857 51.9112 19.225 51.6752C19.4642 51.4397 19.7495 51.255 20.0815 51.1222C20.4132 50.9897 20.772 50.9232 21.1582 50.9232C21.5282 50.9232 21.8792 50.9897 22.2112 51.1222C22.543 51.255 22.8285 51.4397 23.0677 51.6752C23.3067 51.9112 23.4995 52.1875 23.6467 52.5045C23.7932 52.8217 23.8662 53.157 23.8662 53.5107C23.8662 53.8645 23.7932 54.1965 23.6467 54.5057ZM23.7275 48.9552C22.755 47.9675 21.5282 47.4735 20.0472 47.4735C19.1675 47.4735 18.3642 47.632 17.6392 47.9487C16.9137 48.2662 16.2887 48.6975 15.7642 49.2427C15.239 49.7882 14.8301 50.4255 14.5371 51.1555C14.2438 51.885 14.0973 52.6555 14.0973 53.4665C14.0973 54.3367 14.248 55.14 14.5484 55.8767C14.8498 56.614 15.2702 57.2557 15.8107 57.801C16.3502 58.3465 16.9832 58.7702 17.7087 59.0725C18.4335 59.3745 19.229 59.5257 20.0932 59.5257C21.621 59.5257 22.8325 58.9877 23.7275 57.9112V59.172H27.9407V47.8272H23.7275V48.9552Z"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M37.1538 53.6414L33.9821 47.8249H29.1211L34.8618 57.6219L30.8108 64.8314H35.5103L45.0483 47.8249H40.2328L37.1538 53.6414Z"/>
      <path d="M8.59219 24.7934L7.43262 24.4454C6.64498 24.1898 5.41586 23.7948 5.41586 22.7731C5.41586 21.7973 6.52894 21.3095 7.36346 21.3095C7.77534 21.3095 8.18107 21.3731 8.57067 21.4885C9.73216 21.8331 10.9736 21.3061 11.5265 20.227L12.1723 18.9655C12.18 18.9517 12.1742 18.9336 12.1604 18.9263C10.6093 18.0516 8.46655 17.5 6.71413 17.5C3.21353 17.5 0.686539 19.8228 0.686539 23.3768C0.686539 26.7682 2.56459 27.5116 5.41586 28.3242C6.38985 28.603 8.17454 29.0445 8.17454 30.3222C8.17454 31.5529 7.0388 32.0872 5.97221 32.0872C4.41882 32.0872 3.07483 31.2742 1.9387 30.2756L0.0034012 33.9219C-0.0035147 33.9354 0.00032732 33.9519 0.0130065 33.9608C1.79385 35.2057 3.94162 35.9433 6.13473 35.9433C7.89637 35.9433 9.72793 35.4552 11.0961 34.2942C12.487 33.1093 12.9043 31.3204 12.9043 29.5788C12.9043 26.7444 11.0262 25.514 8.59219 24.7934Z"/>
      <path d="M20.137 26.5332C22.2241 26.5332 22.0381 28.5304 22.0381 29.6691V35.4457C22.0381 35.4623 22.0516 35.4758 22.0681 35.4758H23.7456C25.133 35.4758 26.2576 34.3494 26.2576 32.9589V28.1127C26.2576 25.1858 24.9824 23.1648 21.7834 23.1648C20.1424 23.1648 18.9621 23.6499 17.99 25.0796C17.9842 25.0877 17.9754 25.0931 17.9654 25.0931C17.9489 25.0931 17.9347 25.0796 17.9347 25.063V23.5898C17.9347 23.5736 17.9212 23.5598 17.9043 23.5598H13.7452C13.7287 23.5598 13.7152 23.5736 13.7152 23.5898V35.4457C13.7152 35.4623 13.7287 35.4758 13.7452 35.4758H15.4227C16.8105 35.4758 17.9347 34.3494 17.9347 32.9589V29.2976C17.9347 27.8105 18.4215 26.5332 20.137 26.5332Z"/>
      <path d="M34.1294 32.2022C32.4139 32.2022 31.4168 31.0404 31.4168 29.531C31.4168 28.0678 32.4139 26.8367 34.1294 26.8367C35.8453 26.8367 36.842 28.0678 36.842 29.531C36.842 31.0404 35.8453 32.2022 34.1294 32.2022ZM36.7325 23.5614C36.7163 23.5614 36.7025 23.5749 36.7025 23.5915V24.6416C36.7025 24.6701 36.6683 24.6824 36.6502 24.6617C35.765 23.6577 34.3576 23.1661 33.0167 23.1661C29.4927 23.1661 27.0586 26.0933 27.0586 29.5079C27.0586 32.9225 29.5392 35.872 33.0632 35.872C34.4487 35.872 35.9033 35.3423 36.6479 34.167C36.6529 34.1586 36.6629 34.1532 36.6725 34.1532C36.6894 34.1532 36.7025 34.167 36.7025 34.1832V35.4474C36.7025 35.464 36.7163 35.4774 36.7325 35.4774H38.41C39.7977 35.4774 40.9223 34.3507 40.9223 32.9606V23.5915C40.9223 23.5749 40.9093 23.5614 40.8924 23.5614H36.7325Z"/>
      <path d="M49.0064 32.2023C47.2901 32.2023 46.2934 31.0405 46.2934 29.5311C46.2934 28.0678 47.2901 26.8368 49.0064 26.8368C50.7219 26.8368 51.7186 28.0678 51.7186 29.5311C51.7186 31.0405 50.7219 32.2023 49.0064 32.2023ZM50.0726 23.1662C48.6867 23.1662 47.2547 23.7198 46.4878 24.8954C46.4825 24.9035 46.4725 24.9085 46.4632 24.9085C46.4463 24.9085 46.4329 24.8954 46.4329 24.8785V23.5615H42.2134V40.5028H43.9209C45.3083 40.5028 46.4329 39.376 46.4329 37.9859V34.3496L46.4394 34.3473C47.3439 35.3867 48.7543 35.8721 50.1191 35.8721C53.6427 35.8721 56.0771 32.9918 56.0771 29.5542C56.0771 26.1396 53.6197 23.1662 50.0726 23.1662Z"/>
      <path d="M63.6576 26.8368C65.3731 26.8368 66.3705 28.0678 66.3705 29.5311C66.3705 31.0409 65.3731 32.2023 63.6576 32.2023C61.9417 32.2023 60.945 31.0409 60.945 29.5311C60.945 28.0678 61.9417 26.8368 63.6576 26.8368ZM58.5725 40.5028C59.9595 40.5028 61.0845 39.376 61.0845 37.9863V34.4185C61.0845 34.3908 61.1183 34.3785 61.1363 34.3992C62.0427 35.4036 63.4286 35.8725 64.7703 35.8725C68.2943 35.8725 70.7283 32.9918 70.7283 29.5546C70.7283 26.1396 68.2713 23.1666 64.7238 23.1666C63.3383 23.1666 61.9063 23.7197 61.1394 24.8954C61.134 24.9039 61.1244 24.9085 61.1144 24.9085C61.0979 24.9085 61.0845 24.8954 61.0845 24.8785V23.5916C61.0845 23.575 61.071 23.5615 61.0545 23.5615H56.8946C56.8781 23.5615 56.8646 23.575 56.8646 23.5916V40.4727C56.8646 40.4897 56.8781 40.5028 56.8946 40.5028H58.5725Z"/>
      <path d="M75.1861 28.9655C76.3369 28.9655 77.3412 28.186 77.6286 27.0696L80 17.9634H75.6687L72.809 28.9655H75.1861Z"/>
      <path d="M74.2332 35.8707C75.7063 35.8707 76.9023 34.6719 76.9023 33.196C76.9023 31.7201 75.7063 30.5213 74.2332 30.5213C72.7593 30.5213 71.5632 31.7201 71.5632 33.196C71.5632 34.6719 72.7593 35.8707 74.2332 35.8707Z"/>
      <path d="M72.9226 18.185H72.9284L73.2592 19.1097H73.4063L73.7379 18.185H73.7437V19.1097H73.9926V17.964H73.5908L73.3329 18.6419L73.0751 17.964H72.6732V19.1097H72.9226V18.185Z"/>
      <path d="M71.9117 19.111H72.1864V18.1863H72.5345V17.965H71.5632V18.1863H71.9117V19.111Z"/>
    </svg>
  );
}


// DigiPay's mark + «دیجی‌پی» wordmark, from their own SVGs (mydigipay.com
// /bck-assets/images/logos/dp-logo.svg + logo.svg) — never redrawn. On the
// brand-blue button the mark is inverted (white tile, blue chevron) and the
// wordmark takes `currentColor`, the same trick SnappPayLogo uses.
export function DigiPayLogo({ height = 24, tile = WHITE, chevron = BRAND.digipay, style = {} }) {
  return (
    <span aria-label="DigiPay" style={{ display: "inline-flex", alignItems: "center", gap: height * 0.3, flex: "0 0 auto", direction: "ltr", ...style }}>
      <svg viewBox="0 0 77.68 19.2" fill="currentColor" style={{ display: "block", height: height * 0.72, width: "auto" }}>
        <path d="M53.25,13.09h-3.49v3.49h3.49v-3.49Z"/>
        <path d="M62.83,13.09h-6.98v3.49h6.98v-3.49Z"/>
        <path d="M19.2,15.71v2.64h3.49v-1.77h2.63v-3.49h-4.06c-1.14,0-2.06.92-2.06,2.06v.56h0Z"/>
        <path d="M61.08,5.8c0,1.14-.92,2.06-2.06,2.06h-4.92v-2.2C54.1,2.53,51.57,0,48.45,0h-4.81v3.49h4.92c1.14,0,2.06.92,2.06,2.06v2.31h-13.08v3.49h6.1v2.31c0,1.14-.92,2.06-2.06,2.06h-6.36c-1.14,0-2.06-.92-2.06-2.06v-5.8h-3.49v5.69c0,3.13,2.53,5.66,5.66,5.66h6.14c3.12,0,5.66-2.53,5.66-5.66v-2.2h11.8c3.12,0,5.66-2.53,5.66-5.66V1.75h-3.49v4.05h0Z"/>
        <path d="M23.56,5.8c0,1.14-.92,2.06-2.06,2.06H7.86v3.49h6.1v2.31c0,1.14-.92,2.06-2.06,2.06h-6.36c-1.14,0-2.06-.92-2.06-2.06v-5.8H0v5.69c0,3.13,2.53,5.66,5.66,5.66h6.14c3.13,0,5.66-2.53,5.66-5.66v-2.2h3.94c3.13,0,5.66-2.53,5.66-5.66V1.79h-3.49v4h0Z"/>
        <path d="M72.03,0h-5.69v3.49h5.8c1.14,0,2.06.92,2.06,2.06v.25c0,1.14-.92,2.06-2.06,2.06h-5.8v3.49h5.69c3.12,0,5.66-2.53,5.66-5.66v-.03c0-3.13-2.53-5.66-5.66-5.66h0Z"/>
      </svg>
      <svg viewBox="0 0 22 22" style={{ display: "block", height, width: height, flex: "0 0 auto" }}>
        <path d="M16.6341 0H5.36585C2.40237 0 0 2.40237 0 5.36585V16.6341C0 19.5976 2.40237 22 5.36585 22H16.6341C19.5976 22 22 19.5976 22 16.6341V5.36585C22 2.40237 19.5976 0 16.6341 0Z" fill={tile}/>
        <path d="M9.19516 6.10534C10.1918 5.10877 11.8075 5.10877 12.8041 6.10534L17.2332 10.5344C17.4597 10.761 17.4597 11.1282 17.2332 11.3546L16.249 12.3389C16.0224 12.5654 15.6552 12.5654 15.4288 12.3389L11.4097 8.31989C11.1832 8.09337 10.8161 8.09337 10.5895 8.31989L6.57052 12.3389C6.34402 12.5654 5.9768 12.5654 5.7503 12.3389L4.76606 11.3546C4.53957 11.1282 4.53957 10.761 4.76606 10.5344L9.19516 6.10534Z" fill={chevron}/>
      </svg>
    </span>
  );
}


// Audio progress bar (track + gold gradient fill + glowing handle) with click +
// drag scrubbing — the SAME component/look as the admin panel player.
export function AudioProgress({ pct, onSeekFrac, width, style }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  const seekAt = (clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (r && r.width) onSeekFrac(Math.min(1, Math.max(0, (clientX - r.left) / r.width)));
  };
  const onDown = (e) => {
    e.preventDefault();
    setDragging(true);
    seekAt(e.clientX);
    const move = (ev) => seekAt(ev.clientX);
    const up = () => { setDragging(false); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  // Gentle glide on skip/playback, but INSTANT while dragging (so the handle tracks
  // the finger without lag).
  const glide = dragging ? "none" : "0.35s ease";
  return (
    <div ref={ref} onPointerDown={onDown} style={{ direction: "ltr", flex: width ? "none" : 1, width: width || "auto", height: 16, display: "flex", alignItems: "center", cursor: "pointer", position: "relative", minWidth: 24, touchAction: "none", ...style }}>
      <div style={{ position: "relative", width: "100%", height: 5, background: C.border, borderRadius: 3 }}>
        <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${pct}%`, background: `linear-gradient(90deg, ${C.goldDim}, ${C.gold})`, borderRadius: 3, transition: `width ${glide}` }} />
        <div style={{ position: "absolute", top: "50%", left: `${pct}%`, width: 13, height: 13, marginTop: -6.5, marginLeft: -6.5, borderRadius: "50%", background: C.goldLight, boxShadow: `0 0 6px ${C.gold}`, pointerEvents: "none", transition: `left ${glide}` }} />
      </div>
    </div>
  );
}


// «ورود با بازار» — the Bazaar app's sign-in, shown INSIDE the step that needs it
// (adding a child, paying) rather than as a wall at launch (product owner,
// 2026-10-29). `onSignIn` resolves true when signed in, false when the parent
// closed Bazaar's page; the shell then flips `guest` and this card disappears.
export function BazaarSignInCard({ onSignIn, title, note }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function go() {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const ok = await onSignIn();
      if (!ok) setError(t("ورود انجام نشد. دوباره امتحان کنید."));
    } catch (e) {
      setError(e?.code === "bazaar_missing"
        ? t("برای ورود، اپ «بازار» باید روی گوشی نصب و به‌روز باشد.")
        : (e?.message || t("ورود انجام نشد. دوباره امتحان کنید.")));
    } finally { setBusy(false); }
  }
  return (
    <div style={{ background: C.bgCard, border: `1.5px solid ${C.borderGold}`, borderRadius: 20, padding: 18, marginBottom: 16 }}>
      <div style={{ color: C.gold, fontSize: 15.5, fontWeight: 800, marginBottom: 6 }}>{title || t("ورود با حساب بازار")}</div>
      <div style={{ color: C.textMid, fontSize: 13, lineHeight: 1.8, marginBottom: 14 }}>
        {note || t("قصه‌ها و اشتراک فرزندتان به حساب بازار شما وصل می‌شود تا روی هر گوشی‌ای در دسترس باشد. شماره یا رمز لازم نیست.")}
      </div>
      <button onClick={go} disabled={busy} className="press"
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: WHITE, color: C.bgDeep, border: "none", borderRadius: 14, padding: "13px 0", fontSize: 16, fontWeight: 800, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}>
        <img src={BAZAAR_ICON} width={24} height={24} alt="" />
        {busy ? t("در حال ورود...") : t("ورود با بازار")}
      </button>
      {error && <div style={{ color: C.red, fontSize: 13, marginTop: 10, textAlign: "center" }}>{error}</div>}
    </div>
  );
}

// ── The admin's info banner (services/banner.py) ─────────────────────────────
// Text only, with ✕. Closing it hides THIS banner (by id) on this device; new text
// from the admin gets a new id and shows again. Fetched once per mount.

const BANNER_KEY = "zg_banner_closed";

export function InfoBanner() {
  const [b, setB] = useState(null);
  useEffect(() => {
    let alive = true;
    api("/users/banner").then(r => {
      let closed = null;
      try { closed = localStorage.getItem(BANNER_KEY); } catch (_) {}
      if (alive && r?.banner && String(r.banner.id) !== closed) setB(r.banner);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!b) return null;
  const close = () => {
    try { localStorage.setItem(BANNER_KEY, String(b.id)); } catch (_) {}
    setB(null);
  };
  return (
    <div role="status" style={{ display: "flex", alignItems: "flex-start", gap: 10, background: C.bgCard, border: `1px solid ${C.borderGold}`, borderRadius: 16, padding: "12px 14px", marginBottom: 16 }}>
      <div style={{ flex: 1, minWidth: 0, color: C.cream, fontSize: 14, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{b.text}</div>
      <button onClick={close} aria-label="بستن" className="press"
        style={{ background: "none", border: "none", color: C.textMid, cursor: "pointer", padding: 2, flexShrink: 0, display: "flex" }}>
        <X size={18} />
      </button>
    </div>
  );
}

// ── Adly ad card (2026-11-03) — FREE families only, inside Eitaa only ─────────
// Adly's widget refuses to run outside an Eitaa برنامک, and since every host page
// loads the Eitaa SDK (window.Eitaa exists everywhere, parsing the same
// tgWebAppData hash), its own check could be fooled on Telegram/Bale — so the
// HOST is checked here too (hostKey reads what the host injects natively). The
// config (public widget key, barnamk, appRef) comes from platform_info.ads.
// The widget is a singleton bound to ONE container id; re-mounting Home just
// re-inits it on the new element. It hides the container itself when no ad comes.
const ADLY_SRC = "https://myadino.ir/widget/adly-widget.js";
const ADLY_ID = "adly-container";
let adlyScript = null;
function loadAdly() {
  if (window.AdlyWidget) return Promise.resolve(window.AdlyWidget);
  if (!adlyScript) {
    adlyScript = new Promise((resolve, reject) => {
      const el = document.createElement("script");
      el.src = ADLY_SRC; el.async = true;
      el.onload = () => (window.AdlyWidget ? resolve(window.AdlyWidget) : reject(new Error("no AdlyWidget")));
      el.onerror = () => { adlyScript = null; reject(new Error("adly script failed")); };
      document.head.appendChild(el);
    });
  }
  return adlyScript;
}

// Adly paints its card for a LIGHT page (a near-transparent background, and the
// text colour copied from <body>, which reads black here), so on our dark theme it
// vanished. These rules dress it in the app's own card colours.
const ADLY_CSS = `
#${ADLY_ID} .adly-native-card { background: ${C.bgCard} !important; border: 1px solid ${C.borderGold} !important; color: ${C.cream} !important; font-family: inherit !important; }
#${ADLY_ID} .adly-native-card * { color: inherit; }
#${ADLY_ID} .adly-native-card > div > div:last-child { color: ${C.creamDim} !important; opacity: 1 !important; }
#${ADLY_ID} .adly-brand-link { color: ${C.red} !important; }
`;

// PINNED to the bottom of the screen (product owner, 2026-11-03: seen from any part
// of the page), above the host's bottom inset, and an in-flow spacer of the same
// height so the page's last row never hides under it. Nothing shows until an ad has
// actually been drawn — the widget's own «در حال بارگذاری…» is a grey light-theme box.
export function AdSlot({ show = true, above = 0 }) {
  const ads = platformInfo()?.ads;
  const on = show && ads?.provider === "adly" && hostKey() === "eitaa";
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!on) return;
    let alive = true;
    const el = document.getElementById(ADLY_ID);
    if (!document.getElementById("adly-theme")) {
      const st = document.createElement("style");
      st.id = "adly-theme"; st.textContent = ADLY_CSS;
      document.head.appendChild(st);
    }
    // ready = the widget drew its card (it hides the container when no ad comes)
    const check = () => alive && setReady(!!el?.querySelector(".adly-native-card, img") && el.style.display !== "none");
    const mo = el ? new MutationObserver(check) : null;
    mo?.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
    loadAdly().then(W => {
      if (!alive || !document.getElementById(ADLY_ID)) return;
      W.init({ apiKey: ads.api_key, containerId: ADLY_ID, barnamk: ads.barnamk, appRef: ads.app_ref });
    }).catch(() => {});
    return () => { alive = false; mo?.disconnect(); try { window.AdlyWidget?.stopAutoRefresh?.(); } catch (_) {} };
  }, [on]);
  if (!on) return null;
  return (
    <>
      {ready && <div style={{ height: 96 }} aria-hidden="true" />}
      {/* The wrapper is full-width and its bottom padding lies OVER whatever the
          card is pinned above (the questionnaire's «قبلی / بعدی» bar) — so it must
          never take taps itself; only the card does. (2026-11-04: it swallowed «بعدی».) */}
      <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40, pointerEvents: "none",
        opacity: ready ? 1 : 0, transition: "opacity .3s",
        padding: above ? `0 12px ${above + 8}px` : "0 12px calc(10px + var(--tg-safe-bottom, 0px))" }}>
        <div style={{ maxWidth: 406, margin: "0 auto", borderRadius: 14, boxShadow: `0 -6px 24px ${C.bgDeep}`, pointerEvents: ready ? "auto" : "none" }}>
          <div id={ADLY_ID} />
        </div>
      </div>
    </>
  );
}

// ── Guarantee seal (2026-11-03) ──────────────────────────────────────────────
// The gold rosette from the promo banner («تخفیف · به مدت محدود»), here saying
// «۵ روز ضمانت». A scalloped SVG ring (no image file), text in the ink colour.
// `size` is the rosette's diameter; the text scales with it.
export function GuaranteeSeal({ size = 58, days = 5, label, compact = false }) {
  const { t } = useLang();
  const id = useRef(`gs${Math.random().toString(36).slice(2, 8)}`).current;
  const n = 18, r1 = 50, r2 = 44;
  let d = "";
  for (let i = 0; i < n * 2; i++) {
    const a = (Math.PI * i) / n - Math.PI / 2, r = i % 2 ? r2 : r1;
    d += `${i ? "L" : "M"}${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`;
  }
  return (
    <span style={{ position: "relative", display: "inline-flex", width: size, height: size, flexShrink: 0 }} aria-label={label || t("ضمانت بازگشت وجه")}>
      <svg viewBox="0 0 100 100" width={size} height={size} style={{ position: "absolute", inset: 0 }}>
        <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={C.goldLight} /><stop offset=".55" stopColor={C.gold} /><stop offset="1" stopColor={C.goldLight} />
        </linearGradient></defs>
        <path d={d + "Z"} fill={`url(#${id})`} />
        <circle cx="50" cy="50" r="37" fill="none" stroke={C.bgDeep} strokeOpacity=".35" strokeWidth="1.5" strokeDasharray="2 3" />
      </svg>
      <span style={{ position: "relative", margin: "auto", textAlign: "center", color: C.bgDeep, lineHeight: 1.05, fontWeight: 800 }}>
        {compact
          ? <span style={{ display: "block", fontSize: size * 0.46 }}>{toFa(days)}</span>
          : <>
            <span style={{ display: "block", fontSize: size * 0.27 }}>{t("{n} روز", { n: toFa(days) })}</span>
            <span style={{ display: "block", fontSize: size * 0.15, marginTop: 1 }}>{label || t("ضمانت")}</span>
          </>}
      </span>
    </span>
  );
}

